import { useCallback } from "react";
import { useNotification } from "../../../hooks/useNotification";
import { useSubmitSaleInvoiceMutation } from "../../../store/apiSlice/invoiceApiSlice";

const submitErrorMessage = error => {
  const body = error?.data;
  if (!body) return "Failed to submit invoice.";
  if (typeof body.detail === "string" && body.detail.trim()) {
    return body.detail;
  }
  const nested = body.data || body;
  for (const key of ["status", "customer", "non_field_errors"]) {
    const value = nested?.[key];
    if (typeof value === "string" && value.trim()) {
      return value;
    }
    if (Array.isArray(value) && value[0]) {
      return String(value[0]);
    }
  }
  return "Failed to submit invoice.";
};

const submitSuccessMessage = res => {
  if (res?.warning) {
    return res.message ? `${res.message} ${res.warning}` : res.warning;
  }
  return res?.message || "Your invoice has been submitted for approval.";
};

/**
 * Submit a draft sale invoice for approval, independent of sharing.
 *
 * @param {Object} [options]
 * @param {() => void} [options.onComplete] Called after a successful submit.
 */
export const useSubmitSaleInvoice = ({ onComplete } = {}) => {
  const notify = useNotification();
  const [submitSaleInvoice, { isLoading: isSubmitting }] = useSubmitSaleInvoiceMutation();

  const submit = useCallback(
    async invoiceId => {
      if (!invoiceId) return;
      try {
        const res = await submitSaleInvoice({ id: invoiceId }).unwrap();
        notify({ type: "success", message: submitSuccessMessage(res) });
        onComplete?.();
      } catch (error) {
        notify({ type: "error", message: submitErrorMessage(error) });
      }
    },
    [submitSaleInvoice, notify, onComplete],
  );

  return { submit, isSubmitting };
};
